import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../api/apiClient";
import { useAuth } from "../auth/AuthProvider";
import { readStoredSites, writeStoredSites } from "../../auth/siteStorage";

const SessionDataContext = createContext(null);
const DEFAULT_ACCOUNT_SLUGS = ['primergy'];

function siteNames(sites) {
  return sites
    .map((site) => String(site?.name ?? '').trim())
    .filter(Boolean);
}

export function useSessionData() {
  const context = useContext(SessionDataContext);
  if (!context) {
    throw new Error("useSessionData must be used within a SessionDataProvider");
  }
  return context;
}

export function SessionDataProvider({ children }) {
  const { user, isAuthLoading } = useAuth();
  const userSub = user?.sub ?? null;
  const [sites, setSites] = useState(() => readStoredSites(null));
  const [isSessionDataLoading, setIsSessionDataLoading] = useState(false);

  const loadInitialData = useCallback(async () => {
    setIsSessionDataLoading(true);
    try {
      const response = await apiFetch('/api/sites');
      const loadedSites = Array.isArray(response?.sites) ? response.sites : [];
      writeStoredSites(userSub, loadedSites);
      setSites(loadedSites);
      return siteNames(loadedSites);
    } catch {
      return siteNames(readStoredSites(userSub));
    } finally {
      setIsSessionDataLoading(false);
    }
  }, [userSub]);

  useEffect(() => {
    if (isAuthLoading) return undefined;

    const cached = readStoredSites(userSub);
    setSites(cached);
    loadInitialData();
    return undefined;
  }, [isAuthLoading, loadInitialData, userSub]);

  const plants = useMemo(() => siteNames(sites), [sites]);

  const value = useMemo(
    () => ({
      sites,
      accountSlugs: DEFAULT_ACCOUNT_SLUGS,
      plants,
      isSessionDataLoading,
      loadInitialData,
    }),
    [isSessionDataLoading, loadInitialData, plants, sites]
  );

  return <SessionDataContext.Provider value={value}>{children}</SessionDataContext.Provider>;
}
